/* eslint-disable no-param-reassign */
import { Roles } from '@/auth/enum/auth.enum';
import { AuthService } from '@/auth/service/auth.service';
import { AssetLicenseValidation } from '@/interfaces/new-license-request/new-license-request-interfaces';
import { CacheService } from '@/utils/cacheService/cache.service';
import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map, take } from 'rxjs/operators';
import {
  ApprovalApproveContractOwner, ApprovalApproveManager, ApprovalApproveMineManager,
  ApprovalApproveSuperIntendent, ApprovalDelegate, ApprovalDetailList, ApprovalReject,
  ApprovalRequestAdditionalInfo, MunicipalLicence, UpdateAssigned, ValidityTypes,
} from '../../interfaces/approvals/approvals';
import { parseISOString } from '../../utils/parseISOString';

@Injectable({
  providedIn: 'root',
})
export class ApprovalsService {

  constructor(
    private http: HttpClient,
    private auth: AuthService,
    private cacheService: CacheService,
  ) { }

  getRequestApprovalPending(mdmid: string): Observable<ApprovalDetailList[]> {
    const role = this.auth.getRoles()[0];
    const source$ = this.http.get<ApprovalDetailList[]>(`/Requirement/RequirementsForApproval/RoleId/${role.id}/Mdmid/${mdmid}`)
      .pipe(
        map((value) => value.map(({ date, ...otherKeys }) => (
          { ...otherKeys, date: parseISOString(<string><unknown>date) }
        )).sort((a, b) => <number><unknown>a.date - <number><unknown>b.date)),
      );

    return this.cacheService.save<ApprovalDetailList[]>('getRequestApprovalPending', 1, source$);
  }

  private getTotalRequestApprovalCountNew(mdmid: string): Observable<number> {
    const roleId = this.auth.getRoles()[0].id;

    const source$ = this.http.get<{ quantity: number, pageSize: number }>(`/Requirement/RequirementsForApproval/RoleId/${roleId}/Mdmid/${mdmid}/Quantity`).pipe(
      map((x) => x.quantity),
    );

    return this.cacheService.save<number>('getTotalRequestApprovalCount', 1, source$);
  }

  getTotalRequestApprovalCount(mdmid: string): Observable<number> {
    return this.getRequestApprovalPending(mdmid)
      .pipe(map((a) => (a ? a.length : 0)));
  }

  getLicensesType(): Observable<MunicipalLicence[]> {
    const source$ = this.http.get<MunicipalLicence[]>('Common/MunicipalLicense');

    return this.cacheService.save<MunicipalLicence[]>('getLicensesType', 24 * 60, source$);
  }

  rejectLicenseApproval(params: ApprovalReject, rol: Roles) {
    const roleId = this.auth.getRoleIdByName(rol);
    const completeParams = { ...params, approvingRole: +roleId };
    this.clearCachePendingApprovalsAndTasks();
    return this.http.post('Flow/UpdateRequirementStatus', completeParams).pipe(take(1));
  }

  requestAdditionalInfo(params: ApprovalRequestAdditionalInfo, rol: Roles) {
    const roleId = this.auth.getRoleIdByName(rol);
    const completeParams = { ...params, approvingRole: +roleId };
    this.clearCachePendingApprovalsAndTasks();
    return this.http.post('Flow/UpdateRequirementStatus', completeParams)
      .pipe(take(1));
  }

  approveApprovalContractOwner(params: ApprovalApproveContractOwner, rol: Roles) {
    const roleId = this.auth.getRoleIdByName(rol);
    const completeParams = { ...params, approvingRole: +roleId };
    this.clearCachePendingApprovalsAndTasks();
    return this.http.post('Flow/UpdateRequirementStatus', completeParams).pipe(take(1));
  }

  approveApprovalManager(params: ApprovalApproveManager, rol: Roles) {
    const roleId = this.auth.getRoleIdByName(rol);
    if (params.additionalInformation.licenseValidity === 1) {
      params.additionalInformation.licenseValidityTemporaryDays = undefined as any;
    }
    if (params.additionalInformation.licenseValidityTemporaryComment === '') {
      params.additionalInformation.licenseValidityTemporaryComment = undefined as any;
    }
    if (params.additionalInformation.driverRestrictionsComment === '') {
      params.additionalInformation.driverRestrictionsComment = undefined as any;
    }

    const completeParams = { ...params, approvingRole: +roleId };
    this.clearCachePendingApprovalsAndTasks();
    return this.http.post('Flow/UpdateRequirementStatus', completeParams);
  }

  approveApprovalSuperintendent(params: ApprovalApproveSuperIntendent, rol: Roles) {
    const roleId = this.auth.getRoleIdByName(rol);
    const completeParams = { ...params, approvingRole: +roleId };
    this.clearCachePendingApprovalsAndTasks();
    return this.http.post('Flow/UpdateRequirementStatus', completeParams);
  }

  approveApprovalMineManager(params: ApprovalApproveMineManager, rol: Roles) {
    const roleId = this.auth.getRoleIdByName(rol);
    const completeParams = { ...params, approvingRole: +roleId };
    this.clearCachePendingApprovalsAndTasks();
    return this.http.post<ApprovalApproveMineManager>('Flow/UpdateRequirementStatus', completeParams);
  }

  delegateApprovalRequest(params: ApprovalDelegate) {
    this.clearCachePendingApprovalsAndTasks();
    return this.http.post<ApprovalDelegate>('Delegation/Requirement/Create', params).pipe(take(1));
  }

  getValidityTypes(assetId: number): Observable<ValidityTypes[]> {
    const source$ = this.http.get<ValidityTypes[]>(`Common/LicensesValidities?assetId=${assetId}`);

    return this.cacheService.save<MunicipalLicence[]>('getValidityTypes', 24 * 60, source$);
  }

  updateAssigned(requirementId: number, mdmid: string, roleId: number) {
    const body: UpdateAssigned = { assigned: mdmid, assignedRoleId: roleId };
    return this.http.put(`/Requirement/UpdateAssigned/RequirementId/${requirementId}`, body);
  }

  validateInProgressRequestApproval(requirementId: number): Observable<AssetLicenseValidation> {

    return this.http.get<boolean>(`/Requirement/ExistsAssigned?requirementId=${requirementId}`).pipe(map((exist) => ({
      exist,
    })));
  }

  private clearCachePendingApprovalsAndTasks() {
    this.cacheService.remove('getRequestApprovalPending');
    this.cacheService.remove('getTotalTaskPending');
  }
}
